import React from 'react'
import { J2Add } from '@e3dc-react/icons'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { Table } from '@e3dc-react/shell/Elements/Table'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button } from '@mui/material'

import useModals from 'src/Hooks/useModals'
import useCompanyInstallerTableActions from 'src/Libs/company-settings/hooks/use-company-installers-actions'
import {
  CompanyInstallerTableItemModel,
  CompanyInvitationModel,
  CompanySettingsInviteActionMenu,
  CompanySettingsUserActionMenu,
} from 'src/Libs/company-settings'
import { CompanyUserData } from 'src/State/User/UserModel'
import useCompanyUserTableData from '../hooks/use-company-users-table-data'

interface CompanyUsersTableProps {
  companyId: number
  installers: Array<CompanyInstallerTableItemModel>
  loading?: boolean
  companyManager?: number
  companyHasShop: boolean
  companyName: string
  refreshTable: () => void
}
/**
 * This component is used inside CompanyUserPermissions. It displays the table data and have all the actions.
 */
const CompanyUsersTable: React.FC<CompanyUsersTableProps> = ({
  installers,
  loading = false,
  companyId,
  companyManager,
  companyHasShop,
  companyName,
  refreshTable,
}) => {
  const { t } = useCustomTranslation()
  const { openInviteEditor } = useModals()
  const { onChangeShop, onDeleteInstaller, onChangeNotifications, onDeleteInvitation, onReSendInvite, onSendInvite, onChangeManager } =
    useCompanyInstallerTableActions(companyId)

  const { header: tableHeader, data: tableData } = useCompanyUserTableData({
    users: installers,
    companyId,
    onChangeShop,
    onChangeNotifications,
    onReSendInvite,
    companyHasShop,
    companyManager,
  })

  const menuOptions: ((item: CompanyInstallerTableItemModel) => React.ReactNode)[] = [
    item => {
      if (item.isInvite) {
        return (
          <CompanySettingsInviteActionMenu
            item={item.value as CompanyInvitationModel}
            onRemove={onDeleteInvitation}
            onResend={onReSendInvite}
            companyId={companyId}
          />
        )
      }
      if (companyManager === item.id) {
        return
      }
      return (
        <CompanySettingsUserActionMenu
          item={item.value as CompanyUserData}
          onRemove={onDeleteInstaller}
          onChangeManager={(user, changeManagerReason, removeOldManager) =>
            onChangeManager(user, changeManagerReason, removeOldManager, refreshTable)
          }
          companyName={companyName}
          managerId={companyManager}
        />
      )
    },
  ]

  const customFunctionalities = menuOptions.map(func => installers?.map(installer => func(installer)) ?? [])

  if (loading) {
    return <Loading />
  }

  return (
    <Box ml={2} mr={2} mt={1}>
      <Table
        header={tableHeader}
        data={tableData}
        customFunctionalities={customFunctionalities}
        headerElevation={0}
        bodyElevation={0}
        footerElevation={0}
      />
      <Box mb={4} mt={2}>
        <Button
          startIcon={<J2Add />}
          onClick={() =>
            openInviteEditor({
              onSave: onSendInvite,
              size: 'sm',
              title: t('service-dashboard.company.invitation.title'),
            })
          }
        >
          {t('user.company.invitation.inviteUser')}
        </Button>
      </Box>
    </Box>
  )
}

export default CompanyUsersTable
